import Vue from "vue";
import { DeviceTemplate, selectedPowerConsumptionMode } from "./DeviceTemplate";

/**
 * Enum for types of device power usage.
 */
export enum DevicePowerUsageType {
  NONE = "NONE",
  OPERATING_HOURS = "OPERATING_HOURS",
  ELECTRICITY_METER = "ELECTRICITY_METER",
  ELECTRICITY_METER_FOR_OTHER_DEVICES = "ELECTRICITY_METER_FOR_OTHER_DEVICES",
  OPERATING_HOURS_FROM_ASSIGNMENT = "OPERATING_HOURS_FROM_ASSIGNMENT"
}

/**
 * Enum for types of device attributes.
 */
export enum DeviceAttributeType {
  INVENTORY_NUMBER = "INVENTORY_NUMBER",
  COMMENT = "COMMENT",
  METER_READING = "METER_READING",
  POWER_CONSUMPTION_PER_HOUR = "POWER_CONSUMPTION_PER_HOUR",
  OPERATING_HOURS = "OPERATING_HOURS",
  ELECTRICITY_METER_MANUFACTURER = "ELECTRICITY_METER_MANUFACTURER",
  ELECTRICITY_METER_NUMBER = "ELECTRICITY_METER_NUMBER",
  ELECTRICITY_METER_FOR_OTHER_DEVICES = "ELECTRICITY_METER_FOR_OTHER_DEVICES",
  OPERATING_HOURS_FROM_ASSIGNMENT = "OPERATING_HOURS_FROM_ASSIGNMENT"
}

/**
 * Describes a device attribute with its dependency and selectability settings.
 */
export interface DeviceAttribute {
  deviceAttributeType?: DeviceAttributeType;
  isProjectAssignmentDependent: boolean;
  canHaveDefaultValue: boolean;
  canBeSelected: boolean;
}

export interface DeviceAttributeInput {
  deviceAttributeType: DeviceAttributeType;
  input?: string | null;
}

export interface DevicePowerConsumptionInput {
  startValue?: number | null;
  endValue?: number | null;
  userForReport?: boolean;
}

export interface DeviceAssignmentPowerInput {
  operatingHours?: number | null;
  userForReport?: boolean;
  wasConfirmed?: boolean;
}

export const DEVICE_POWER_INPUT_ATTRIBUTE_TYPES: DeviceAttributeType[] = [
  DeviceAttributeType.METER_READING,
  DeviceAttributeType.OPERATING_HOURS,
  DeviceAttributeType.OPERATING_HOURS_FROM_ASSIGNMENT,
];

/**
 * Determines the device attribute type for power usage based on the device template.
 * @param {DeviceTemplate} deviceTemplate - The device template to check.
 * @returns {DevicePowerUsageType} The determined device power usage type.
 */
export function getDeviceAttributeTypeFromDeviceTemplate(deviceTemplate: DeviceTemplate): DevicePowerUsageType {
  switch (selectedPowerConsumptionMode(deviceTemplate)) {
    case DeviceAttributeType.METER_READING: {
      const canBeUsedAsElectricityMeterForOtherDevices = deviceTemplate.deviceAttributes.some(
        (container) =>
          container.attributeType === DeviceAttributeType.ELECTRICITY_METER_FOR_OTHER_DEVICES
      );

      return canBeUsedAsElectricityMeterForOtherDevices
        ? DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES
        : DevicePowerUsageType.ELECTRICITY_METER;
    }

    case DeviceAttributeType.OPERATING_HOURS_FROM_ASSIGNMENT:
      return DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT;

    case DeviceAttributeType.OPERATING_HOURS:
      return DevicePowerUsageType.OPERATING_HOURS;

    default:
      return DevicePowerUsageType.NONE;
  }
}

/**
 * Determines the corresponding device attribute type based on a given device power usage type.
 * This function maps specific power usage types to attribute types that describe the kind of data
 * or measurement the device reports (e.g., electricity meter readings, operating hours).
 *
 * @param {DevicePowerUsageType} powerUsageType - The power usage type of the device, used to determine the attribute type.
 * @returns {DeviceAttributeType | undefined} The corresponding device attribute type, or undefined if the power usage type does not match any known categories.
 */
export function getDeviceAttributeTypeFromDevicePowerUsageType(powerUsageType: DevicePowerUsageType): DeviceAttributeType | undefined {
  switch (powerUsageType) {
    case DevicePowerUsageType.ELECTRICITY_METER:
      return DeviceAttributeType.METER_READING;
    
    case DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES:
      return DeviceAttributeType.ELECTRICITY_METER_FOR_OTHER_DEVICES;
    
    case DevicePowerUsageType.OPERATING_HOURS:
      return DeviceAttributeType.OPERATING_HOURS;
    
    case DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT:
      return DeviceAttributeType.OPERATING_HOURS_FROM_ASSIGNMENT;
    
    default:
      return undefined;
  }
}

/**
 * Retrieves a numerical value associated with a specific device power usage type.
 * This function assigns a unique integer to different types of power usage measurements,
 * which could be useful for sorting, display order, or other logical decisions based on
 * the type of power usage reported by a device.
 *
 * @param {DevicePowerUsageType} devicePowerUsageType - The type of power usage as defined for the device.
 * @returns {number} A numeric value representing the device power usage type.
 */
export function getDevicePowerUsageNumber(devicePowerUsageType: DevicePowerUsageType) {
  switch (devicePowerUsageType) {
    case DevicePowerUsageType.ELECTRICITY_METER:
      return 2;
    
    case DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES:
      return 3;
    
    case DevicePowerUsageType.OPERATING_HOURS:
      return 1;
    
    case DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT:
      return 4;
    
    default:
      return 0;
  }
}

/**
 * Gets the device attribute type by name.
 * @param {string} deviceAttribute - Name of the device attribute.
 * @returns {DeviceAttributeType | undefined} The corresponding device attribute type, or undefined if not found.
 */
export function getDeviceAttributeTypeByName(deviceAttribute: string): DeviceAttributeType | undefined {
  switch (deviceAttribute) {
    case "INVENTORY_NUMBER":
      return DeviceAttributeType.INVENTORY_NUMBER;
    case "COMMENT":
      return DeviceAttributeType.COMMENT;
    case "METER_READING":
      return DeviceAttributeType.METER_READING;
    case "POWER_CONSUMPTION_PER_HOUR":
      return DeviceAttributeType.POWER_CONSUMPTION_PER_HOUR;
    case "OPERATING_HOURS":
      return DeviceAttributeType.OPERATING_HOURS;
    case "ELECTRICITY_METER_MANUFACTURER":
      return DeviceAttributeType.ELECTRICITY_METER_MANUFACTURER;
    case "ELECTRICITY_METER_NUMBER":
      return DeviceAttributeType.ELECTRICITY_METER_NUMBER;
    case "ELECTRICITY_METER_FOR_OTHER_DEVICES":
      return DeviceAttributeType.ELECTRICITY_METER_FOR_OTHER_DEVICES;
    case "OPERATING_HOURS_FROM_ASSIGNMENT":
      return DeviceAttributeType.OPERATING_HOURS_FROM_ASSIGNMENT;
    default:
      return undefined;
  }
}

export function getDevicePowerUsageNameByType(vue: Vue, devicePowerUsageType: DevicePowerUsageType): string {
  switch (devicePowerUsageType) {
    case DevicePowerUsageType.OPERATING_HOURS:
      return vue.$t("allDevices.operatingHours").toString();
    case DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT:
      return vue.$t("allDevices.operatingHoursFromAssignment").toString();
    case DevicePowerUsageType.ELECTRICITY_METER:
      return vue.$t("allDevices.electricityMeter").toString();
    case DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES:
      return vue.$t("allDevices.electricityMeterForOtherDevices").toString();
    default:
      return vue.$t("allDevices.noDevicePowerUsage").toString();
  }
}

export function getDevicePowerUsageTypeByName(vue: Vue, devicePowerUsageName: string): DevicePowerUsageType {
  if (vue.$t("allDevices.operatingHours").toString() === devicePowerUsageName) {
      return DevicePowerUsageType.OPERATING_HOURS;
  } else if (vue.$t("allDevices.operatingHoursFromAssignment").toString() === devicePowerUsageName) {
      return DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT;
  } else if (vue.$t("allDevices.electricityMeter").toString() === devicePowerUsageName) {
      return DevicePowerUsageType.ELECTRICITY_METER;
  } else if (vue.$t("allDevices.electricityMeterForOtherDevices").toString() === devicePowerUsageName) {
      return DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES;
  } else {
      return DevicePowerUsageType.NONE;
  }
}

/**
 * Provides a user-friendly name for each type of device power usage, which can be used for label creation in
 * user interfaces or documentation. This function maps device power usage types to corresponding
 * descriptive names that are more understandable to end-users or for display purposes in template creation.
 *
 * @param {DevicePowerUsageType} devicePowerUsageType - The specific type of power usage for which a name is required.
 * @returns {string} A string representing the user-friendly name associated with the device power usage type.
 */
export function getDevicePowerUsageNameForDefaultTemplateCreation(devicePowerUsageType: DevicePowerUsageType) {
  switch (devicePowerUsageType) {
    case DevicePowerUsageType.OPERATING_HOURS:
      return "Operating Hours";
    case DevicePowerUsageType.OPERATING_HOURS_FROM_ASSIGNMENT:
      return "Assignment";
    case DevicePowerUsageType.ELECTRICITY_METER:
      return "Power";
    case DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES:
      return "Power Extern";
    default:
      return "None";
  }
}
